import { useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Typography,
  MenuItem,
  Select,
  FormControl,
  IconButton
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Header from '../components/Header/Header.ui';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/UploadFileOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import getProfileInfo, { getAllParts, getVehicleModel } from './details.modal';

const steps = ['معلومات العميل', 'تفاصيل المركبة', 'صور المركبة', 'القطع المطلوبة', 'ملاحظات إضافية'];

const cities = [
  { key: 'NABLUS', ar: 'نابلس' },
  { key: 'RAMALLAH', ar: 'رام الله' },
  { key: 'TULKARM', ar: 'طولكرم' },
  { key: 'QALQILYA', ar: 'قلقيلية' },
  { key: 'JENIN', ar: 'جنين' },
  { key: 'BETHLEHEM', ar: 'بيت لحم' },
  { key: 'HEBRON', ar: 'الخليل' },
  { key: 'JERICHO', ar: 'أريحا' },
  { key: 'JERUSALEM', ar: 'القدس' },
  { key: 'TUBAS', ar: 'طوباس' },
  { key: 'SELFIT', ar: 'سلفيت' },
];

const CutDetailsScreen = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [vehicleImages, setVehicleImages] = useState([]);
  const [partsList, setPartsList] = useState([]);
  const [partForm, setPartForm] = useState({ name: '', quantity: '' });
  const [parts, setParts] = useState([]);
  const [notes, setNotes] = useState('');
  const [vehicleModels, setVehicleModels] = useState([]);
  const [vehicleSubModels, setVehicleSubModels] = useState([]);
  const [client, setClient] = useState({});
  const [phone, setPhone] = useState('');
  const [loadingClient, setLoadingClient] = useState(false);
const { code } = useParams();

  // ======== DISABLE UI until code is loaded =========
  const [isLocked, setIsLocked] = useState(true);

  const [customerInfo, setCustomerInfo] = useState({
    phone: '',
    name: '',
    city: '',
  });

  const [vehicleInfo, setVehicleInfo] = useState({
    type: '',
    model: '',
    year: '',
    plateNumber: '',
    chassisNumber: '',
  });

  const [clientCode, setClientCode] = useState("");

  useEffect(() => {
  if (code) {
    setClientCode(code); 
  }
}, [code]);

  // 🟦 Auto search when code changes
  useEffect(() => {
    if (!clientCode) return;

    const delay = setTimeout(() => {
      fetchClientByCode();
    }, 600);

    return () => clearTimeout(delay);
  }, [clientCode]);

  // ============================
  //  Fetch client by code (Auto)
  // ============================
  const fetchClientByCode = async () => {
    if (!clientCode) return;

    setLoadingClient(true);
    setIsLocked(true);

    try {
      const clientData = await getProfileInfo(clientCode);

      if (!clientData) {
        setIsLocked(true);
        setLoadingClient(false);
        return;
      }

      setClient(clientData);

      if (clientData?.phone?.[0]) {
        const formattedPhone =
          '+' + clientData.phone[0].countryCode + clientData.phone[0].number;

        setPhone(formattedPhone);
        setCustomerInfo({
          phone: formattedPhone,
          name: clientData.name || '',
          city: '',
        });
      }

      setParts(await getAllParts());
      setVehicleModels(await getVehicleModel());

      setIsLocked(false); // Unlock the UI
    } catch (err) {
      console.error(err);
      setIsLocked(true);
    }

    setLoadingClient(false);
  };

  // ============================
  //  Vehicle submodels fetch
  // ============================
  const fetchSubModels = (vehicleModelID) => {
    if (!vehicleModelID) return;

    const requestData = {
      jsonrpc: '2.0',
      method: 'vehicle.get_vehicleSubModel',
      id: Math.floor(Math.random() * 1000000),
      params: { filters: { vehicleModelID } },
    };

    fetch('https://api.tpamotor.com/rpc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData),
    })
      .then((res) => res.json())
      .then((response) => {
        setVehicleSubModels(response?.result || []);
      })
      .catch(() => setVehicleSubModels([]));
  };

  // ============================
  //   Form Handlers
  // ============================
  const handleChange = (e, setter) => {
    const { name, value } = e.target;
    setter((prev) => ({ ...prev, [name]: value }));
  };

  const handlePartChange = (e) => {
    const { name, value } = e.target;
    setPartForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddPart = () => {
    if (!partForm.name || !partForm.quantity) return;
    setPartsList((prev) => [...prev, partForm]);
    setPartForm({ name: '', quantity: '' });
  };

  const handleRemovePart = (index) => {
    setPartsList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveImage = (index) => {
    setVehicleImages((prev) => prev.filter((_, i) => i !== index));
  };

  // ============================
  //   Validation
  // ============================
  const validateStep = () => {
    const newErrors = {};
    if (activeStep === 0) {
      if (!customerInfo.phone) newErrors.phone = 'رقم الهاتف مطلوب';
      if (!customerInfo.name) newErrors.name = 'الاسم مطلوب';
      if (!customerInfo.city) newErrors.city = 'المدينة مطلوبة';
    }
    if (activeStep === 1) {
      if (!vehicleInfo.type) newErrors.type = 'نوع المركبة مطلوب';
      if (!vehicleInfo.model) newErrors.model = 'الطراز مطلوب';
      if (!vehicleInfo.year) newErrors.year = 'السنة مطلوبة';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validateStep()) setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  // ============================
  //   Upload Image
  // ============================
  const uploadImage = async (file) => {
    try {
      const uploadData = new FormData();
      uploadData.append('compress', 'true');
      uploadData.append('type', 'clientsClaims');
      uploadData.append('clientClaim', 'true');
      uploadData.append('sellerID', client?.sellerID || 'sellerID');
      uploadData.append('clientID', client?.id || 'clientID');
      uploadData.append('file', file, file.name);

      const response = await fetch('https://api.tpamotor.com/upload', {
        method: 'POST',
        body: uploadData,
      });

      const data = await response.json();
      return data?.fileName || null;
    } catch (error) {
      return null;
    }
  };

  // ============================
  //   Submit final claim
  // ============================
  const handleSubmit = async () => {
    if (!validateStep()) return;

    const uploadedImages = [];
    for (const file of vehicleImages) {
      const fileName = await uploadImage(file);
      if (fileName) uploadedImages.push(fileName);
    }

    const requestBody = {
      jsonrpc: '2.0',
      method: 'sellerClient.add_claim',
      id: Math.floor(Math.random() * 1000000),
      params: {
        client: { id: client?.id },
        seller: { id: client?.sellerID },
        vehicle: {
          vehicleModelID: vehicleInfo.type,
          vehicleSubModelID: vehicleInfo.model,
          licenseNumber: vehicleInfo.plateNumber,
          year: vehicleInfo.year,
          chassis: vehicleInfo.chassisNumber,
          fuel:"GAS"
        },
        parts: partsList.map((part) => ({
          name: part.name,
          amount: part.quantity,
          partID: parts.find((p) => p.id === part.name)?.id || null,
        })),
        note: notes,
        images: uploadedImages.map((name) => ({ name })),
      },
    };

    try {
      const res = await fetch('https://api.tpamotor.com/rpc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const response = await res.json();

      if (response?.result) {
        alert('✅ تم إرسال الطلب بنجاح');
      } else {
        alert('⚠️ حدث خطأ أثناء الإرسال');
      }
    } catch (error) {
      alert('❌ فشل إرسال الطلب');
    }
  };

  return (
    <Box sx={{ width: '100%', direction: 'rtl', fontFamily: 'Cairo', minHeight: '100vh', position: 'relative', pb: '80px' }}>
      <Header Step={activeStep + 1} />

      {/* ============================
          CLIENT CODE INPUT (Auto Search)
      ============================ */}
      <Box sx={{ p: 3, borderBottom: "1px solid #ddd" }}>
        <Typography fontWeight="bold" variant="h6" sx={{ mb: 2 }}>
          أدخل  الكود
        </Typography>

        <TextField
          label="أدخل الكود"
          fullWidth
          value={clientCode}
          onChange={(e) => setClientCode(e.target.value)}
          sx={{ mb: 2 }}
        />

        {loadingClient && (
          <Typography color="primary">جاري البحث...</Typography>
        )}
      </Box>

      {/* ========== DISABLED UNTIL CLIENT LOADED ========== */}
      <Box
        sx={{
          p: 4,
          opacity: isLocked ? 0.4 : 1,
          pointerEvents: isLocked ? "none" : "auto",
        }}
      >
        {/* Step 0: Customer Info */}
        {activeStep === 0 && (
          <form onSubmit={handleNext}>
            <Typography variant="subtitle1" fontWeight="bold">معلومات العميل</Typography>

            <Typography sx={{ mt: 2 }}>رقم الهاتف</Typography>
            <PhoneInput
              country={'sa'}
              disabled
              value={customerInfo.phone}
              inputStyle={{ height: '50px', width: '100%', borderRadius: '12px' }}
            />
            {errors.phone && <Typography color="error">{errors.phone}</Typography>}

            <Typography sx={{ mt: 2 }}>الاسم الكامل</Typography>
            <TextField
              name="name"
              value={customerInfo.name}
              onChange={(e) => handleChange(e, setCustomerInfo)}
              fullWidth
            />

            <Typography sx={{ mt: 2 }}>المدينة</Typography>
            <FormControl fullWidth>
              <Select
                name="city"
                value={customerInfo.city}
                onChange={(e) => handleChange(e, setCustomerInfo)}
              >
                <MenuItem value=""><em>اختر المدينة</em></MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city.key} value={city.key}>{city.ar}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </form>
        )}

        {/* Step 1: Vehicle Info */}
        {activeStep === 1 && (
          <form onSubmit={handleNext}>
            <Typography variant="subtitle1" fontWeight="bold">تفاصيل المركبة</Typography>

            <Typography sx={{ mt: 2 }}>النوع</Typography>
            <Select
              name="type"
              fullWidth
              value={vehicleInfo.type}
              onChange={(e) => {
                handleChange(e, setVehicleInfo);
                fetchSubModels(e.target.value);
              }}
            >
              <MenuItem value=""><em>اختر نوع المركبة</em></MenuItem>
              {Array.isArray(vehicleModels) && vehicleModels.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
              ))}
            </Select>

            <Typography sx={{ mt: 2 }}>الطراز</Typography>
            <Select
              name="model"
              fullWidth
              value={vehicleInfo.model}
              onChange={(e) => handleChange(e, setVehicleInfo)}
            >
              <MenuItem value=""><em>اختر الطراز</em></MenuItem>
              {vehicleSubModels.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.alias?.ar || s.name}</MenuItem>
              ))}
            </Select>

            <TextField name="year" label="سنة الإنتاج" value={vehicleInfo.year} onChange={(e) => handleChange(e, setVehicleInfo)} fullWidth sx={{ mt: 2 }} />
            <TextField name="plateNumber" label="رقم اللوحة" value={vehicleInfo.plateNumber} onChange={(e) => handleChange(e, setVehicleInfo)} fullWidth sx={{ mt: 2 }} />
            <TextField name="chassisNumber" label="رقم الشصي" value={vehicleInfo.chassisNumber} onChange={(e) => handleChange(e, setVehicleInfo)} fullWidth sx={{ mt: 2 }} />
          </form>
        )}

        {/* Step 2: Upload Images */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">صور المركبة</Typography>
            <Button variant="outlined" component="label" sx={{ width: '100%', height: '150px', mb: 2 }}>
              تحميل الصور
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={(e) => setVehicleImages(Array.from(e.target.files))}
              />
              <UploadIcon />
            </Button>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {vehicleImages.map((file, index) => (
                <Box key={index} sx={{ width: 100, height: 100, position: 'relative' }}>
                  <img src={URL.createObjectURL(file)} alt="" style={{ width: '100%', height: '100%', borderRadius: 8 }} />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(index)}
                    sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'white' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Step 3: Parts */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">القطع المطلوبة</Typography>
            <Select
              name="name"
              fullWidth
              sx={{ mt: 2 }}
              value={partForm.name}
              onChange={handlePartChange}
            >
              <MenuItem value=""><em>اختر القطعة</em></MenuItem>
              {Array.isArray(parts) && parts.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.alias?.ar || p.name}</MenuItem>
              ))}
            </Select>
            <TextField
              name="quantity"
              type="number"
              label="الكمية"
              fullWidth
              sx={{ mt: 2 }}
              value={partForm.quantity}
              onChange={handlePartChange}
            />
            <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={handleAddPart}>إضافة القطعة</Button>

            <Box sx={{ mt: 3 }}>
              {partsList.map((part, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, border: '1px solid #ccc', borderRadius: 8, mt: 1 }}>
                  <Typography>{parts.find((p) => p.id === part.name)?.alias?.ar || part.name} - {part.quantity}</Typography>
                  <IconButton color="error" onClick={() => handleRemovePart(i)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Step 4: Notes */}
        {activeStep === 4 && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">ملاحظات إضافية</Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="اكتب ملاحظاتك هنا..."
              sx={{ mt: 2 }}
            />
          </Box>
        )}
      </Box>

      {/* ========== Navigation Buttons ========== */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          bgcolor: 'background.paper',
          p: 2,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          display: isLocked ? 'none' : 'flex',
          gap: 2,
        }}
      >
        {activeStep > 0 && (
          <Button variant="outlined" onClick={handleBack} sx={{ width: '50%', height: '50px' }}>
            رجوع
          </Button>
        )}
        {activeStep < 4 ? (
          <Button variant="contained" onClick={handleNext} sx={{ width: activeStep > 0 ? '50%' : '100%', height: '50px' }}>
            التالي
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ width: '100%', height: '50px' }}>
            إرسال الطلب
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CutDetailsScreen;
